import type { MapsCandidateLocation } from '../models/maps.types';

/**
 * Candidate-location predicates for the results-page location filters
 * (Test Results 7-15-26). Policy mirror of the backend: MISSING LOCATION DATA
 * NEVER EXCLUDES — corpus rows are often state-only with a null country, so
 * both predicates fire only on a POSITIVELY KNOWN mismatch.
 */

/**
 * Mirror of US_ALIASES in apps/MJAPI/src/resolvers/maps/structuredFit.ts —
 * keep the two lists in sync (no shared package between MAPS and MJAPI yet).
 */
const US_ALIASES = new Set(['us', 'usa', 'u.s.', 'u.s.a.', 'united states', 'united states of america']);

/** 50 states + DC, name → USPS code, for same-state comparison (the corpus
 *  stores "CA" or "California"; the brief's region is LLM-extracted text).
 *  Exported since the STN round: the US-state filter facets and the derived
 *  timezone map (utils/timezone.ts) key off the same code space. */
export const US_STATE_CODES: Record<string, string> = {
    alabama: 'al', alaska: 'ak', arizona: 'az', arkansas: 'ar', california: 'ca',
    colorado: 'co', connecticut: 'ct', delaware: 'de', florida: 'fl', georgia: 'ga',
    hawaii: 'hi', idaho: 'id', illinois: 'il', indiana: 'in', iowa: 'ia',
    kansas: 'ks', kentucky: 'ky', louisiana: 'la', maine: 'me', maryland: 'md',
    massachusetts: 'ma', michigan: 'mi', minnesota: 'mn', mississippi: 'ms',
    missouri: 'mo', montana: 'mt', nebraska: 'ne', nevada: 'nv',
    'new hampshire': 'nh', 'new jersey': 'nj', 'new mexico': 'nm', 'new york': 'ny',
    'north carolina': 'nc', 'north dakota': 'nd', ohio: 'oh', oklahoma: 'ok',
    oregon: 'or', pennsylvania: 'pa', 'rhode island': 'ri', 'south carolina': 'sc',
    'south dakota': 'sd', tennessee: 'tn', texas: 'tx', utah: 'ut', vermont: 'vt',
    virginia: 'va', washington: 'wa', 'west virginia': 'wv', wisconsin: 'wi',
    wyoming: 'wy', 'district of columbia': 'dc',
};

/** Full name or code → lowercase USPS code (unknown values pass through). */
export function NormalizeUsState(value: string): string {
    const v = value.trim().toLowerCase();
    return US_STATE_CODES[v] || v;
}

/** Lowercase USPS code → display name ("ca" → "California"); null when the
 *  code isn't one of the 50+DC (never render an unvetted corpus string). */
export function StateDisplayName(code: string): string | null {
    const target = code.trim().toLowerCase();
    for (const [name, c] of Object.entries(US_STATE_CODES)) {
        if (c === target) {
            return name.replace(/\b[a-z]/g, (ch) => ch.toUpperCase()).replace(/\bOf\b/, 'of');
        }
    }
    return null;
}

/**
 * True ONLY when the candidate's country is present and not a US alias.
 * Missing/blank country never excludes (state-only rows leave country null).
 */
export function IsKnownNonUs(loc: MapsCandidateLocation | null | undefined): boolean {
    const country = loc?.country?.trim();
    if (!country) return false;
    return !US_ALIASES.has(country.toLowerCase());
}

/**
 * True ONLY when the candidate's state is present and differs from the
 * position's state after normalization. Missing state never excludes.
 */
export function IsKnownOtherState(
    loc: MapsCandidateLocation | null | undefined,
    positionState: string
): boolean {
    const state = loc?.state?.trim();
    if (!state) return false;
    return NormalizeUsState(state) !== NormalizeUsState(positionState);
}
